import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { useAuth } from "hooks/useAuth";
import { logout } from "slices/auth/reducer";
import { fetchAgencyMasterThunk, fetchUser } from "slices/auth/thunk";
import { changeSidebarTheme } from "slices/thunks";
import Loader from "Components/Common/Loader";
import NoAgency from "pages/NoAgency";
import { AppDispatch, RootState } from "store";
import usePageTracking from "hooks/usePageTracking";
import { fetchMembershipThunk } from "slices/membership/thunk";
import { analyticsSetUser, analyticsSetUserProperties } from "services/analytics";
import { hasValidAgency } from "utils/agency";

const AuthProtected = (props: { children: React.ReactNode }) => {
    const dispatch = useDispatch<AppDispatch>();
    const { isAuthenticated, loading } = useAuth();

    const [userFetched, setUserFetched] = useState(false);
    const [masterFetchedForAgencyId, setMasterFetchedForAgencyId] = useState<string | null>(null);
    const location = useLocation();

    const userId = useSelector((state: RootState) => state.auth.user?.uuid);
    const activePlan = useSelector((state: RootState) => state.membershipState.membership.data?.active_plan);
    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    const agencyReady = useSelector((state: RootState) => hasValidAgency(state.auth));

    usePageTracking(userId);

    useEffect(() => {
        if (!isAuthenticated) {
            dispatch(logout());
            setUserFetched(true);
            return;
        }

        dispatch(fetchUser())
            .unwrap()
            .then((profile) => {
                const agencies = profile.user?.agencies ?? [];
                if (agencies.length > 0) {
                    return dispatch(fetchMembershipThunk({ force: true }));
                }
            })
            .catch(() => {})
            .finally(() => setUserFetched(true));
    }, [isAuthenticated, dispatch]);

    useEffect(() => {
        if (selectedAgencyId) {
            analyticsSetUser(selectedAgencyId);
        }
    }, [selectedAgencyId]);

    useEffect(() => {
        if (agencyReady && userFetched && selectedAgencyId) {
            dispatch(fetchAgencyMasterThunk())
                .unwrap()
                .then((master) => {
                    dispatch(changeSidebarTheme(master?.partner?.menu_style === 'white-blue' ? 'light' : 'dark'));
                })
                .catch(() => {
                    dispatch(changeSidebarTheme('dark'));
                })
                .finally(() => setMasterFetchedForAgencyId(selectedAgencyId));
        }
    }, [selectedAgencyId, agencyReady, userFetched, dispatch]);

    useEffect(() => {
        if (activePlan) {
            analyticsSetUserProperties({
                plan: activePlan?.plan?.name,
            });
        }
    }, [activePlan]);

    if (!isAuthenticated && !loading) {
        return <Navigate to={{ pathname: "/login", search: location.search }} />;
    }

    if (loading || !userFetched) return <Loader />;

    if (!agencyReady) {
        return <NoAgency />;
    }

    if (!masterFetchedForAgencyId || masterFetchedForAgencyId !== selectedAgencyId) return <Loader />;

    return <>{props.children}</>;
};

export default AuthProtected;
